import React, { useState, useRef } from 'react';
import { useUser } from '../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import './VideoUpload.css';

const VideoUpload = () => {
  const { currentUser } = useUser();
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStage, setUploadStage] = useState('select');
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  
  const channels = [
    { id: 'health-01', channelName: 'HealthyLiving365', category: 'Health & Wellness', owner: 'Dr. Sarah Johnson', icon: '🏥', color: '#10B981', subscribers: '2.3M' },
    { id: 'movies-01', channelName: 'CineMaxHD', category: 'Movies & Entertainment', owner: 'CineMax Studios', icon: '🎬', color: '#EC4899', subscribers: '5.1M' },
    { id: 'music-01', channelName: 'SoundWaveMusic', category: 'Music', owner: 'SoundWave Productions', icon: '🎵', color: '#8B5CF6', subscribers: '8.7M' },
    { id: 'news-01', channelName: 'GlobalNewsLive', category: 'News & Politics', owner: 'Global News Network', icon: '📰', color: '#EF4444', subscribers: '3.2M' },
    { id: 'gaming-01', channelName: 'GameProCentral', category: 'Gaming', owner: 'GamePro Studios', icon: '🎮', color: '#6366F1', subscribers: '12.4M' },
    { id: 'education-01', channelName: 'EduTechAcademy', category: 'Education', owner: 'EduTech Foundation', icon: '📚', color: '#F59E0B', subscribers: '4.5M' },
    { id: 'sports-01', channelName: 'SportsManiaTV', category: 'Sports', owner: 'SportsMania Inc.', icon: '⚽', color: '#14B8A6', subscribers: '6.8M' },
    { id: 'comedy-01', channelName: 'LaughTrackTV', category: 'Comedy', owner: 'LaughTrack Studios', icon: '😂', color: '#F97316', subscribers: '9.2M' },
    { id: 'lifestyle-01', channelName: 'LivingBetterDaily', category: 'Lifestyle', owner: 'Living Better Co.', icon: '✨', color: '#A855F7', subscribers: '3.9M' },
    { id: 'tech-01', channelName: 'TechGuruOfficial', category: 'Technology', owner: 'TechGuru', icon: '💻', color: '#3B82F6', subscribers: '7.5M' }
  ];

  const [metadata, setMetadata] = useState({
    title: '',
    description: '',
    tags: '',
    channel: '',
    category: '',
    language: 'en',
    visibility: 'public',
    monetization: true,
    ageRestriction: false,
    comments: true,
    thumbnail: null,
    investmentEnabled: true,
    investmentRequired: '',
    totalShares: '100',
    sharePrice: '',
    investorRevenueSplit: '50',
    investmentDeadline: '',
    useOfFunds: '',
    roiTimeline: ''
  });

  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const CHUNK_SIZE = 5 * 1024 * 1024;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('video/')) {
        handleFileSelection(file);
      } else {
        setError('Please select a video file');
      }
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelection(e.target.files[0]);
    }
  };

  const handleFileSelection = (file) => {
    if (file.size > 5000 * 1024 * 1024) {
      setError('File size must be less than 5GB');
      return;
    }
    setSelectedFile(file);
    setError('');
    // Auto-fill title from filename
    const titleFromFile = file.name.replace(/\.[^/.]+$/, "").replace(/_/g, ' ');
    setMetadata(prev => ({ ...prev, title: titleFromFile }));
    setUploadStage('details');
  };

  const handleThumbnailSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type.startsWith('image/')) {
        setMetadata(prev => ({ ...prev, thumbnail: file }));
      }
    }
  };

  const uploadChunk = async (chunk, chunkIndex, uploadId, token) => {
    const formData = new FormData();
    formData.append('chunk', chunk);
    formData.append('uploadId', uploadId);
    formData.append('chunkIndex', chunkIndex.toString());

    const response = await fetch('/api/upload/chunk', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });

    if (!response.ok) throw new Error(`Failed to upload chunk ${chunkIndex}`);
    return response.json();
  };

  const handleUpload = async () => {
    if (!metadata.title || !metadata.channel) {
      setError('Title and channel are required');
      return;
    }

    setUploadStage('uploading');
    setIsUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const totalChunks = Math.ceil(selectedFile.size / CHUNK_SIZE);

      // Initialize upload
      await fetch('/api/upload/initialize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          uploadId,
          fileName: selectedFile.name,
          fileSize: selectedFile.size,
          totalChunks,
          mimeType: selectedFile.type,
          metadata
        })
      });

      // Upload chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, selectedFile.size);
        const chunk = selectedFile.slice(start, end);
        await uploadChunk(chunk, i, uploadId, token);
        setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
      }

      // Complete upload
      await fetch('/api/upload/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ uploadId })
      });

      setUploadStage('complete');
    } catch (err) {
      console.error('Upload error:', err);
      setError('Upload failed. Please try again.');
      setUploadStage('details');
    } finally {
      setIsUploading(false);
    }
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadStage('select');
    setMetadata({
      title: '',
      description: '',
      tags: '',
      channel: '',
      category: '',
      language: 'en',
      visibility: 'public',
      monetization: true,
      ageRestriction: false,
      comments: true,
      thumbnail: null,
      investmentEnabled: true,
    investmentRequired: '',
    totalShares: '100',
    sharePrice: '',
    investorRevenueSplit: '50',
    investmentDeadline: '',
    useOfFunds: '',
    roiTimeline: ''
    });
  };

  return (
    <div className="upload-container-modern">
      <div className="upload-header-bar">
        <button onClick={() => navigate('/dashboard')} className="back-btn">
          ← Back to Dashboard
        </button>
        <h2>Upload Video</h2>
        <div className="user-info">
          {currentUser?.name || currentUser?.email}
        </div>
      </div>

      <div className="upload-content-area">
        {uploadStage === 'select' && (
          <div className="upload-select-stage">
            <div 
              className={`dropzone ${dragActive ? 'drag-active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <div className="dropzone-content">
                <div className="upload-icon">
                  <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="12" y1="18" x2="12" y2="12"></line>
                    <line x1="9" y1="15" x2="15" y2="15"></line>
                  </svg>
                </div>
                <h3>Drag and drop your video here</h3>
                <p>or click to browse from your device</p>
                <div className="file-requirements">
                  <div className="requirement-item">
                    <span className="req-icon">📹</span>
                    <span>MP4, MOV, AVI, MKV</span>
                  </div>
                  <div className="requirement-item">
                    <span className="req-icon">💾</span>
                    <span>Max 5GB</span>
                  </div>
                  <div className="requirement-item">
                    <span className="req-icon">��</span>
                    <span>Up to 4K resolution</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="upload-benefits">
              <h3>Why upload to Choice Network?</h3>
              <div className="benefits-grid">
                <div className="benefit-card">
                  <span className="benefit-icon">💰</span>
                  <h4>Monetization</h4>
                  <p>Earn from views and investments</p>
                </div>
                <div className="benefit-card">
                  <span className="benefit-icon">📈</span>
                  <h4>Analytics</h4>
                  <p>Detailed performance insights</p>
                </div>
                <div className="benefit-card">
                  <span className="benefit-icon">🌍</span>
                  <h4>Global Reach</h4>
                  <p>10 channels, millions of viewers</p>
                </div>
                <div className="benefit-card">
                  <span className="benefit-icon">🤝</span>
                  <h4>Investment</h4>
                  <p>Get funding for your content</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {uploadStage === 'details' && (
          <div className="details-form">
            <div className="form-progress">
              <div className="progress-step active">1. Details</div>
              <div className="progress-step">2. Upload</div>
              <div className="progress-step">3. Complete</div>
            </div>

            <div className="selected-file-info">
              <div className="file-preview">
                <video src={URL.createObjectURL(selectedFile)} style={{ width: '100px', height: '60px', objectFit: 'cover' }} />
              </div>
              <div className="file-details">
                <h4>{selectedFile.name}</h4>
                <p>{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</p>
              </div>
              <button onClick={resetUpload} className="change-btn">Change File</button>
            </div>

            <div className="form-grid">
              <div className="form-main">
                <div className="form-section">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={metadata.title}
                    onChange={(e) => setMetadata({...metadata, title: e.target.value})}
                    placeholder="Enter a descriptive title"
                    className="modern-input"
                    maxLength="100"
                  />
                  <span className="char-count">{metadata.title.length}/100</span>
                </div>

                <div className="form-section">
                  <label>Description</label>
                  <textarea
                    value={metadata.description}
                    onChange={(e) => setMetadata({...metadata, description: e.target.value})}
                    placeholder="Tell viewers about your video..."
                    rows="5"
                    className="modern-input"
                    maxLength="5000"
                  />
                  <span className="char-count">{metadata.description.length}/5000</span>
                </div>

                <div className="form-section">
                  <label>Channel *</label>
                  <div className="channel-grid-compact">
                    {channels.map(channel => (
                      <div
                        key={channel.id}
                        className={`channel-card-compact ${metadata.channel === channel.id ? 'selected' : ''}`}
                        onClick={() => setMetadata({...metadata, channel: channel.id})}
                        style={{ borderColor: metadata.channel === channel.id ? channel.color : '#e5e7eb' }}
                      >
                        <div className="channel-header">
                          <span style={{ fontSize: '20px' }}>{channel.icon}</span>
                          <div className="channel-text">
                            <div className="channel-name">{channel.channelName}</div>
                            <div className="channel-meta">{channel.category} • {channel.subscribers}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-section">
                  <label>Tags</label>
                  <input
                    type="text"
                    value={metadata.tags}
                    onChange={(e) => setMetadata({...metadata, tags: e.target.value})}
                    placeholder="Add tags separated by commas"
                    className="modern-input"
                  />
                  <p className="field-hint">Tags help people discover your video</p>
                </div>

                <div className="form-section">
                  <label>Thumbnail</label>
                  <div className="thumbnail-upload">
                    <input
                      ref={thumbnailInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailSelect}
                      style={{ display: 'none' }}
                    />
                    <button 
                      type="button"
                      onClick={() => thumbnailInputRef.current?.click()}
                      className="thumbnail-btn"
                    >
                      {metadata.thumbnail ? 'Change Thumbnail' : 'Upload Thumbnail'}
                    </button>
                    {metadata.thumbnail && <span className="thumb-selected">✓ {metadata.thumbnail.name}</span>}
                  </div>
                </div>
              </div>

              <div className="form-sidebar">
                <div className="sidebar-section">
                  <h4>Visibility & Access</h4>
                  <select 
                    value={metadata.visibility}
                    onChange={(e) => setMetadata({...metadata, visibility: e.target.value})}
                    className="modern-select"
                  >
                    <option value="public">Public</option>
                    <option value="unlisted">Unlisted</option>
                    <option value="private">Private</option>
                    <option value="scheduled">Scheduled</option>
                  </select>
                </div>

                <div className="sidebar-section">
                  <h4>Language</h4>
                  <select 
                    value={metadata.language}
                    onChange={(e) => setMetadata({...metadata, language: e.target.value})}
                    className="modern-select"
                  >
                    <option value="en">English</option>
                    <option value="hi">Hindi</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="ar">Arabic</option>
                    <option value="pt">Portuguese</option>
                    <option value="ru">Russian</option>
                    <option value="ja">Japanese</option>
                    <option value="de">German</option>
                  </select>
                </div>

                <div className="sidebar-section">
                  <h4>Monetization</h4>
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={metadata.monetization}
                      onChange={(e) => setMetadata({...metadata, monetization: e.target.checked})}
                    />
                    <span>Enable ads</span>
                  </label>
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={metadata.investmentEnabled}
                      onChange={(e) => setMetadata({...metadata, investmentEnabled: e.target.checked})}
                    />
                    <span>Allow investments</span>
                  </label>
                {metadata.investmentEnabled && (
  <div className="investment-details">
    <div className="investment-field">
      <label>Investment Required ($)</label>
      <input
        type="number"
        placeholder="Total funding needed"
        value={metadata.investmentRequired}
        onChange={(e) => setMetadata({...metadata, investmentRequired: e.target.value})}
        className="modern-input small"
      />
    </div>
    <div className="investment-field">
      <label>Total Shares Available</label>
      <input
        type="number"
        placeholder="100"
        value={metadata.totalShares}
        onChange={(e) => setMetadata({...metadata, totalShares: e.target.value})}
        className="modern-input small"
      />
    </div>
    <div className="investment-field">
      <label>Price Per Share</label>
      <input
        type="text"
        value={metadata.totalShares && metadata.investmentRequired ? 
          '$' + (metadata.investmentRequired / metadata.totalShares).toFixed(2) : 
          'Auto-calculated'}
        disabled
        className="modern-input small"
        style={{backgroundColor: '#f9fafb'}}
      />
    </div>
    <div className="investment-field">
      <label>Investor Revenue Share (%)</label>
      <input
        type="number"
        placeholder="50"
        value={metadata.investorRevenueSplit}
        onChange={(e) => setMetadata({...metadata, investorRevenueSplit: e.target.value})}
        className="modern-input small"
      />
    </div>
    <div className="investment-field">
      <label>Investment Deadline</label>
      <input
        type="date"
        value={metadata.investmentDeadline}
        onChange={(e) => setMetadata({...metadata, investmentDeadline: e.target.value})}
        className="modern-input small"
      />
    </div>
    <div className="investment-field">
      <label>Use of Funds</label>
      <textarea
        placeholder="Equipment, production, marketing, etc."
        value={metadata.useOfFunds}
        onChange={(e) => setMetadata({...metadata, useOfFunds: e.target.value})}
        className="modern-input small"
        rows="3"
      />
    </div>
    <div className="investment-field">
      <label>Expected ROI Timeline</label>
      <select
        value={metadata.roiTimeline}
        onChange={(e) => setMetadata({...metadata, roiTimeline: e.target.value})}
        className="modern-input small"
      >
        <option value="">Select timeline</option>
        <option value="3-months">3 months</option>
        <option value="6-months">6 months</option>
        <option value="1-year">1 year</option>
        <option value="2-years">2 years</option>
      </select>
    </div>
  </div>
)}
                </div>

                <div className="sidebar-section">
                  <h4>Settings</h4>
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={metadata.comments}
                      onChange={(e) => setMetadata({...metadata, comments: e.target.checked})}
                    />
                    <span>Allow comments</span>
                  </label>
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={metadata.ageRestriction}
                      onChange={(e) => setMetadata({...metadata, ageRestriction: e.target.checked})}
                    />
                    <span>Age restriction</span>
                  </label>
                </div>
              </div>
            </div>

            {error && <div className="error-alert">{error}</div>}

            <div className="form-actions">
              <button onClick={resetUpload} className="btn-secondary">Cancel</button>
              <button onClick={handleUpload} className="btn-primary">
                Start Upload
              </button>
            </div>
          </div>
        )}

        {uploadStage === 'uploading' && (
          <div className="upload-progress-container">
            <div className="progress-icon animated">⬆️</div>
            <h3>Uploading to {channels.find(c => c.id === metadata.channel)?.channelName}</h3>
            <div className="progress-details">
              <p className="file-name">{selectedFile.name}</p>
              <p className="file-size">{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</p>
            </div>
            <div className="progress-bar-modern">
              <div className="progress-fill-modern" style={{ width: `${uploadProgress}%` }} />
            </div>
            <p className="progress-text">{uploadProgress}%</p>
            <p className="progress-hint">Please don't close this window</p>
          </div>
        )}

        {uploadStage === 'complete' && (
          <div className="upload-success">
            <div className="success-icon">✅</div>
            <h3>Video Uploaded Successfully!</h3>
            <div className="success-details">
              <div className="detail-item">
                <span className="detail-label">Title:</span>
                <span>{metadata.title}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Channel:</span>
                <span>{channels.find(c => c.id === metadata.channel)?.channelName}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Status:</span>
                <span className="status-badge">Processing</span>
              </div>
            </div>
            <p className="success-message">
              Your video is being processed and will be available shortly on the channel.
            </p>
            <div className="success-actions">
              <button onClick={resetUpload} className="btn-secondary">
                Upload Another Video
              </button>
              <button onClick={() => navigate('/dashboard')} className="btn-primary">
                Go to Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoUpload;
